import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import GuideButton from '../../common/guide/GuideButton';
import { canUseFixedOrders, useManagerProductionPermissions } from '../../manager/useManagerProductionPermissions';
import InventoryStyleListTable from '../../accounting/inventory/components/InventoryStyleListTable';
import FixedOrdersSection from '../components/orders/FixedOrdersSection';
import ProductionOrderFilters from '../components/orders/ProductionOrderFilters';
import ProductionOrderStats from '../components/orders/ProductionOrderStats';
import { buildProductionOrderColumns } from '../components/orders/productionOrderColumns';
import { useProductionNav } from '../context/ProductionNavContext';
import { useProductionPermissions } from '../context/ProductionPermissionsContext';
import ProductionSettingsDialog from '../dialogs/ProductionSettingsDialog';
import { useFixedOrderList } from '../hooks/useFixedOrderList';
import { useProductionOrderList } from '../hooks/useProductionOrderList';

type OrdersTab = 'orders' | 'fixed';

const ProductionOrders: React.FC = () => {
  const { t } = useTranslation('production');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const nav = useProductionNav();
  const { userDetails } = useAuth();
  const perms = useProductionPermissions();
  const { canCreate } = perms;
  const managerPerms = useManagerProductionPermissions(userDetails?.role === 'manager' && !perms.branchId);
  const usePanelPerms = Boolean(perms.branchId) || userDetails?.role !== 'manager';
  const stepsAllowed = usePanelPerms ? perms.steps : managerPerms.steps;
  // Yönetici: "Sabit üretim yapabilir" ya da taslak + tamamlama. Admin/alt admin için hep açık.
  const effectivePerms = usePanelPerms ? perms : managerPerms;
  const fixedAllowed = canUseFixedOrders(effectivePerms);
  const canManageFixed = effectivePerms.canCreate;
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: OrdersTab = fixedAllowed && searchParams.get('tab') === 'fixed' ? 'fixed' : 'orders';
  const selectTab = (next: OrdersTab) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'fixed') params.set('tab', 'fixed');
    else params.delete('tab');
    setSearchParams(params, { replace: true });
  };
  const list = useProductionOrderList();
  const fixedList = useFixedOrderList(fixedAllowed);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const canEditSettings = userDetails?.role !== 'manager';
  const columns = useMemo(
    () => buildProductionOrderColumns(t, list.hasBranches, isMobile, list.opsOn),
    [t, list.hasBranches, isMobile, list.opsOn],
  );

  if (list.moduleDisabled) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{t('errors.PRODUCTION_MODULE_DISABLED')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 0 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <PrecisionManufacturingIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" fontWeight="bold">
            {t('orders.title')}
          </Typography>
          <GuideButton guideKey="production.orders" sx={{ ml: 0.5 }} />
          {list.opsOn && stepsAllowed ? (
            <Button sx={{ ml: 1 }} variant="outlined" onClick={() => nav.openWorkshop()}>
              {t('workshop.openWorkshop')}
            </Button>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
          {canEditSettings ? (
            <Tooltip title={t('settings.open')}>
              <IconButton onClick={() => setSettingsOpen(true)} aria-label={t('settings.open')}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={t('orders.refresh')}>
            <span>
              <IconButton
                onClick={tab === 'fixed' ? () => void fixedList.reload() : list.retry}
                disabled={tab === 'fixed' ? fixedList.loading : list.loading}
                aria-label={t('orders.refresh')}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          {tab === 'orders' ? (
            <Button
              variant="outlined"
              startIcon={list.exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              onClick={list.exportExcel}
              disabled={list.exporting}
            >
              {t('orders.exportExcel')}
            </Button>
          ) : null}
          {canCreate && tab === 'orders' ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => nav.openNew()}>
              {t('orders.new')}
            </Button>
          ) : null}
        </Box>
      </Box>

      {fixedAllowed ? (
        <Tabs
          value={tab}
          onChange={(_, next: OrdersTab) => selectTab(next)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="orders" label={t('fixedOrders.tabOrders')} />
          <Tab
            value="fixed"
            label={fixedList.loading && fixedList.rows.length === 0
              ? t('fixedOrders.sectionTitle')
              : t('fixedOrders.tabFixed', { count: fixedList.rows.length })}
          />
        </Tabs>
      ) : null}

      {tab === 'fixed' ? (
        <FixedOrdersSection
          list={fixedList}
          canProduce={fixedAllowed}
          canManage={canManageFixed}
          hasBranches={list.hasBranches}
          onProduced={list.retry}
        />
      ) : (
        <>
          <ProductionOrderStats
            summary={list.summary}
            loading={list.summaryLoading}
            onSelectStatus={list.setStatusFilter}
          />

          <ProductionOrderFilters
            searchTerm={list.searchTerm}
            onSearchTermChange={list.setSearchTerm}
            statusFilter={list.statusFilter}
            onStatusFilterChange={list.setStatusFilter}
            dateFrom={list.dateFrom}
            dateTo={list.dateTo}
            onDateFromChange={list.setDateFrom}
            onDateToChange={list.setDateTo}
            warehouseId={list.warehouseId}
            warehouses={list.warehouses}
            onWarehouseChange={list.setWarehouseId}
            rootOnly={list.rootOnly}
            onRootOnlyChange={list.setRootOnly}
            filtersDirty={list.filtersDirty}
            onClearFilters={list.clearFilters}
            showOpsStatuses={list.opsOn}
          />

          {list.error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={(
                <Button color="inherit" size="small" onClick={list.retry}>
                  {t('orders.retry')}
                </Button>
              )}
            >
              {list.error}
            </Alert>
          )}

          {list.exportNotice && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={list.dismissExportNotice}>
              {list.exportNotice}
            </Alert>
          )}

          <InventoryStyleListTable
            columns={columns}
            rows={list.rows}
            getRowId={(row) => row.id}
            loading={list.loading}
            totalCount={list.totalCount}
            page={list.page}
            rowsPerPage={list.rowsPerPage}
            onPageChange={(_, newPage) => list.setPage(newPage)}
            onRowsPerPageChange={list.onRowsPerPageChange}
            onSort={list.onSort}
            sortField={list.sortColumn}
            sortDirection={list.sortDirection}
            onRowClick={(row) => nav.openOrder(row.id)}
            clickable
            emptyMessage={list.filtersDirty ? t('orders.emptyFiltered') : t('orders.empty')}
            ariaLabel={t('orders.title')}
            columnResizeStorageKey="production-orders"
            rowsPerPageOptions={[10, 25, 50, 100]}
            toolbarInfo={t('orders.recordCount', { count: list.totalCount })}
          />
        </>
      )}
      <ProductionSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </Box>
  );
};

export default ProductionOrders;
